import md5 from "blueimp-md5";

/** @description 复制文本 */
export const copyText = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

// 根据类型获取值
const getValueByType = (value: any) => {
  if (typeof value === "string") {
    return `"${value}"`;
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => getValueByType(item)).join(",")}]`;
  }
  return value;
};
/**
 * @description 格式化graphql数据格式
 * @param requestData 请求数据
 * @param responseData 响应数据 最多支持嵌套两层
 * @param cutomDataKey 自定义接收数据key
 * @returns
 */
export const formatGraphqlData = (
  requestData: { resultKey: string; data: any },
  responseData: { dataKeys: string[]; [key: string]: string[] },
  cutomDataKey?: string,
) => {
  const { resultKey = "", data = {} } = requestData;
  const { dataKeys = [] } = responseData;
  // 获取请求参数
  const _dataArr = [];
  Object.keys(data).forEach((key) => {
    _dataArr.push({ key, value: getValueByType(data[key]) });
  });
  const _data = _dataArr.map((item) => `${item.key}:${item.value}`).join(",");
  // 获取接收参数
  const _responseArr = [];
  dataKeys.forEach((myKey) => {
    const childKeys = responseData?.[myKey];
    if (childKeys) {
      const _childResult = [];
      (childKeys || []).forEach((val) => {
        const lastKeys = responseData?.[val];
        if (lastKeys) {
          _childResult.push(`${val} {\n${(lastKeys || []).join("\n")}\n}`);
        } else {
          _childResult.push(`${val}`);
        }
      });
      _responseArr.push(`${myKey} {\n${_childResult.join("\n")}\n}`);
    } else {
      _responseArr.push(`${myKey}`);
    }
  });

  const _response = _responseArr.join("\n");
  const _result = `{${resultKey}(${_data}){ ${_response}${cutomDataKey ?? ""}\n}}`;
  return _result;
};

export function pageData(items, pageSize) {
  const total = items.length;
  const pageCount = Math.ceil(total / pageSize);
  return {
    total,
    pageSize,
    pageCount,
    getData: (page) => {
      if (page > pageCount) {
        page = pageCount;
      }
      if (page < 1) {
        page = 1;
      }
      const pageNo = page - 1;
      const startIndex = pageNo * pageSize;
      let endIndex = startIndex + pageSize;
      if (endIndex > total) {
        endIndex = total;
      }
      const data = items.slice(startIndex, endIndex);
      return data;
    },
  };
}

export function transMediaList(data) {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data.map((item) => {
      const basicInfo = item.MediaBasicInfo;
      const fileBasicInfo = item.FileInfoList[0].FileBasicInfo;
      const mediaId = basicInfo.MediaId;
      const result = {
        mediaId,
        mediaType: undefined,
        video: undefined,
        audio: undefined,
        image: undefined,
      };
      const mediaType = basicInfo.MediaType;
      result.mediaType = mediaType;

      if (mediaType === "video") {
        result.video = {
          title: fileBasicInfo.FileName,
          duration: Number(fileBasicInfo.Duration),
          // 源视频的宽高、码率等数据，用于推荐合成数据，不传入或是0时无推荐数据
          width: Number(fileBasicInfo.Width) || 0,
          height: Number(fileBasicInfo.Height) || 0,
          bitrate: Number(fileBasicInfo.Bitrate) || 0,
          coverUrl: basicInfo.CoverURL,
        };
        const spriteImages = basicInfo.SpriteImages;
        if (spriteImages) {
          try {
            const spriteArr = JSON.parse(spriteImages);
            const sprite = spriteArr[0];
            const config = JSON.parse(sprite.Config);
            result.video.spriteConfig = {
              num: config.Num,
              lines: config.SpriteSnapshotConfig?.Lines,
              cols: config.SpriteSnapshotConfig?.Columns,
              cellWidth: config.SpriteSnapshotConfig?.CellWidth,
              cellHeight: config.SpriteSnapshotConfig?.CellHeight,
            };
            result.video.sprites = sprite.SnapshotUrlList;
          } catch (e) {
            console.log(e);
          }
        }
      } else if (mediaType === "audio") {
        result.audio = {
          title: fileBasicInfo.FileName,
          duration: Number(fileBasicInfo.Duration),
          coverURL: "", // 您可以给音频文件一个默认的封面图
        };
      } else if (mediaType === "image") {
        result.image = {
          title: fileBasicInfo.FileName,
          coverUrl: fileBasicInfo.FileUrl,
          // 图片的宽高等数据，用于推荐合成数据，不传入或是0时无推荐数据
          width: Number(fileBasicInfo.Width) || 0,
          height: Number(fileBasicInfo.Height) || 0,
        };
      }

      return result;
    });
  } else {
    return [data];
  }
}

export const genEditorContent = (data: string[]) => {
  return data.length
    ? data.map((item) => {
        return {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: item,
                },
              ],
            },
          ],
        };
      })
    : [
        {
          type: "listItem",
          content: [
            {
              type: "paragraph",
            },
          ],
        },
      ];
};

/** @description 安全字符串生成器 */
export const SecureStringGenerator = (() => {
  // 获取浏览器指纹
  const getFingerprint = () => {
    const navigatorInfo = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      navigator.platform,
      navigator.hardwareConcurrency, // CPU核心数
    ];
    return navigatorInfo.join("-"); // 合并信息
  };

  // 基于浏览器指纹生成加密字符串
  const generate = (length) => {
    const fingerprint = getFingerprint();
    let hash = 0;

    // 简单哈希算法（字符串转数值）
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // 保证为32位整数
    }

    // 将哈希值转换为字符串
    let result = "";
    while (result.length < length) {
      hash = (hash * 31) & 0xffffffff; // 更新哈希值
      const charCode = (Math.abs(hash) % 94) + 33; // 映射到可打印字符范围 [33, 126]
      result += String.fromCharCode(charCode);
    }

    return md5(result.slice(0, length)); // 返回指定长度的字符串
  };

  return { generate };
})();

/**
 * 文件下载
 * @param {string} url - 文件地址（本地/网络地址都可以）
 * @param {string} fileName - 下载后的文件名（带后缀）
 */
export const downloadFile = (url: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName; // 关键：设置下载名
  a.style.display = "none";
  document.body.appendChild(a);
  a.click(); // 模拟点击
  document.body.removeChild(a); // 下载后移除
};
